import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  archiveToilRequest,
  deleteToilRequest,
  getToilRequests,
  requestToilForEmployee,
  reviewToilRequest,
} from '../services/toil-manager.service';
import {
  GetToilRequestsParams,
  ToilForEmployeeInput,
  ToilStatus,
} from '../types/toil-manager.types';

const invalidateToilQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-manage-toil-requests'] });

export const useGetToilRequests = (params: GetToilRequestsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-manage-toil-requests', params],
    queryFn: () => getToilRequests(params),
    staleTime: 0,
  });
};

export const useReviewToilRequest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; status: ToilStatus; note?: string }) =>
      reviewToilRequest(params),
    onSuccess: (result) => {
      invalidateToilQueries(queryClient);
      toast({ title: t(result.Data?.MessageKey ?? 'TOIL_REQUEST_UPDATED') });
    },
  });
};

export const useArchiveToilRequest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => archiveToilRequest(itemId),
    onSuccess: () => {
      invalidateToilQueries(queryClient);
      toast({ title: t('TOIL_REQUEST_ARCHIVED') });
    },
  });
};

export const useDeleteToilRequest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deleteToilRequest(itemId),
    onSuccess: () => {
      invalidateToilQueries(queryClient);
      toast({ title: t('TOIL_REQUEST_DELETED') });
    },
  });
};

/** TOIL filed for an employee; a refusal throws and is toasted by the global mutation error handler. */
export const useRequestToilForEmployee = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: ToilForEmployeeInput) => requestToilForEmployee(input),
    onSuccess: (result) => {
      invalidateToilQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ['hr-toil-requests'] });
      toast({ title: t(result.Data?.MessageKey ?? 'TOIL_REQUEST_SUBMITTED') });
    },
  });
};
